import { createContext, useContext } from 'react';
import type { RuleTreeNode, RulesReply } from '../api-types';

/** The words a title needs that are not in the rules themselves (translated by the caller). */
export interface TitleWords {
  readonly naming: string;
  readonly type: string;
  readonly code: string;
}

const CUT = 60;

/** A convention's text, cut at about 60 characters on a word with «…». */
export function shorten(text: string): string {
  if (text.length <= CUT) return text;
  const head = text.slice(0, CUT);
  const space = head.lastIndexOf(' ');
  return `${(space > CUT / 2 ? head.slice(0, space) : head).trimEnd()}…`;
}

function addTree(map: Map<string, string>, nodes: readonly RuleTreeNode[] | undefined) {
  for (const node of nodes ?? []) {
    map.set(node.section, `${node.code} ${node.name}`);
    addTree(map, node.children);
  }
}

/** Section → the rule's name, as the board writes it («Tipo CTR · Contratto»). */
export function ruleTitles(rules: RulesReply, words: TitleWords): Map<string, string> {
  const map = new Map<string, string>();
  for (const drive of rules.drives) {
    map.set(drive.section, `${drive.code} ${drive.name}`);
    addTree(map, drive.tree);
  }
  map.set(rules.naming.section, words.naming);
  for (const part of rules.naming.parts) map.set(part.section, part.label);
  for (const type of rules.types)
    map.set(type.section, `${words.type} ${type.code} · ${type.label}`);
  for (const code of rules.codes)
    map.set(code.section, `${words.code} ${code.code} · ${code.label}`);
  for (const convention of rules.conventions) map.set(convention.section, shorten(convention.text));
  return map;
}

/** The titles the overview loaded once; empty until the rules arrive (no link meanwhile). */
export const RuleTitlesContext = createContext<ReadonlyMap<string, string>>(new Map());

/** The title of a finding's rule, or `undefined` when it has none or the rules do not name it. */
export function useRuleTitle(section: string | null): string | undefined {
  const titles = useContext(RuleTitlesContext);
  return section === null ? undefined : titles.get(section);
}
